import { viewSectionStandar } from "@/utils";
import { findBarRoomInZoneList, isBarRoomReservation } from "@/utils/venueRoomServiceMode";

function persistAccountsZoneFilter(venueId, selectedZone, showBarraList) {
	if (!venueId || typeof localStorage === "undefined") {
		return;
	}
	try {
		localStorage.setItem(
			`tpv-accounts-zone:${venueId}`,
			JSON.stringify({ selectedZone, showBarraList }),
		);
	} catch {
		/* ignore */
	}
}

function persistSearchProductsByCategories(venueId, enabled) {
	if (!venueId || typeof localStorage === "undefined") {
		return;
	}
	try {
		localStorage.setItem(`tpv-search-by-categories:${venueId}`, JSON.stringify(Boolean(enabled)));
	} catch {
		/* ignore */
	}
}

const getDefaultState = () => ({
	isLoading: false,
	viewSectionAccounts: "allAccounts",
  viewSectionItems: "quickAccess",

	/** Accounts list zone filter (persisted per venue in localStorage). */
	accountsZoneSelectedZone: "Barra",
	accountsZoneShowBarraList: true,

	/** Product search overlay: group results by menu/category (persisted per venue). */
	searchProductsByCategories: false,

	accountSelectedId: null,
	newAccount: null,
	isChangeTable: false,
	descriptionZone: null,

	unifyAccounts: {
		originReservationId: null,
		destinationReservationId: null,
	},

	destinationAccountProductsOrders: null,
	listProductsSelected: [],
	partialPaymentTotal: 0,
	partialPaymentProductOrderIds: [],
	isAccountSelectedForUnify: false,
	isAddProductsCartInAccount: false,
	isAccountMoveProducts: false,

	selectedTables: [],
});

const state = getDefaultState();

const getters = {
	isLoading: (state) => {
		return state.isLoading;
	},
	partialPaymentTotal: (state) => {
		return state.partialPaymentTotal
	},
	partialPaymentProductOrderIds: (state) => state.partialPaymentProductOrderIds,
	viewSectionAccounts: (state) => {
		return state.viewSectionAccounts;
	},
	viewSectionItems: (state) => {
		return state.viewSectionItems;
	},

	accountSelectedId: (state) => {
		return state.accountSelectedId;
	},
	newAccount: (state) => {
		return state.newAccount;
	},
	isChangeTable: (state) => {
		return state.isChangeTable;
	},

	descriptionZone: (state) => {
		return state.descriptionZone;
	},
	unifyAccounts: (state) => {
		return state.unifyAccounts;
	},
	destinationAccountProductsOrders: (state) => {
		return state.destinationAccountProductsOrders;
	},

	listProductsSelected: (state) => {
		return state.listProductsSelected;
	},

	isAccountSelectedForUnify: (state) => {
		return state.isAccountSelectedForUnify;
	},

	isAddProductsCartInAccount: (state) => {
		return state.isAddProductsCartInAccount;
	},
	isAccountMoveProducts: (state) => {
		return state.isAccountMoveProducts;
	},

	selectedTables: (state) => state.selectedTables,

	accountsZoneSelectedZone: (state) => state.accountsZoneSelectedZone,
	accountsZoneShowBarraList: (state) => state.accountsZoneShowBarraList,
	searchProductsByCategories: (state) => state.searchProductsByCategories,
};

const mutations = {
	setIsLoading: (state, payload) => {
		state.isLoading = payload;
	},
	setPartialPaymentTotal(state, total) {
		state.partialPaymentTotal = Number(total) || 0;
	},
	setPartialPaymentProductOrderIds(state, ids) {
		state.partialPaymentProductOrderIds = Array.isArray(ids) ? ids : [];
	},
	setViewSectionAccounts(state, sectionAccounts) {
		state.viewSectionAccounts = sectionAccounts;
	},
	setViewSectionItems(state, sectionItems) {
		state.viewSectionItems = sectionItems;
	},

	setAccountSelectedId(state, accountId) {
		state.accountSelectedId = accountId;
	},
	setNewAccount(state, newAccount) {
		state.newAccount = newAccount;
	},

	updateNewAccount(state, payload) {
		state.newAccount = {
			...state.newAccount,
			...payload,
		};
	},

	setIsChangeTable(state, payload) {
		state.isChangeTable = payload;
	},
	setDescriptionZone(state, payload) {
		state.descriptionZone = payload;
	},

	setDestinationAccountProductsOrders(state, payload) {
		state.destinationAccountProductsOrders = payload;
	},
	setIsAccountMoveProducts(state, payload) {
		state.isAccountMoveProducts = payload;
	},

	setListProductsMoveAccount(state, payload) {
		state.listProductsSelected = payload;
	},
	addListProductsSelected(state, productId) {
		if (!state.listProductsSelected.includes(productId)) {
			state.listProductsSelected.push(productId);
		}
	},
	removeListProductsSelected(state, productId) {
		state.listProductsSelected = state.listProductsSelected.filter((id) => id !== productId);
	},
	clearPartialPaymentState(state) {
		state.partialPaymentProductOrderIds = [];
		state.partialPaymentTotal = 0;
	},
	clearListProductsSelected(state) {
		state.listProductsSelected = [];
		state.partialPaymentProductOrderIds = [];
		state.partialPaymentTotal = 0;
	},

	setUnifyAccounts(state, payload) {
		state.unifyAccounts = {
			...state.unifyAccounts,
			...payload,
		};
	},
	setIsAccountSelectedForUnify(state, payload) {
		state.isAccountSelectedForUnify = payload;
	},
	setIsAddProductsCartInAccount(state, payload) {
		state.isAddProductsCartInAccount = payload;
	},

	setAccountsZoneFilter(state, { selectedZone, showBarraList, venueId }) {
		state.accountsZoneSelectedZone = selectedZone;
		state.accountsZoneShowBarraList = showBarraList;
		persistAccountsZoneFilter(venueId, selectedZone, showBarraList);
	},

	setSearchProductsByCategories(state, { enabled, venueId }) {
		state.searchProductsByCategories = Boolean(enabled);
		persistSearchProductsByCategories(venueId, state.searchProductsByCategories);
	},

	hydrateSearchProductsByCategoriesFromStorage(state, venueId) {
		if (!venueId || typeof localStorage === "undefined") {
			state.searchProductsByCategories = false;
			return;
		}
		try {
			const raw = localStorage.getItem(`tpv-search-by-categories:${venueId}`);
			state.searchProductsByCategories = raw ? JSON.parse(raw) === true : false;
		} catch {
			state.searchProductsByCategories = false;
		}
	},

	hydrateAccountsZoneFromStorage(state, { venueId, zoneReservation }) {
		const zones = Array.isArray(zoneReservation) ? zoneReservation : [];

		let persisted = null;
		if (venueId && typeof localStorage !== "undefined") {
			try {
				const raw = localStorage.getItem(`tpv-accounts-zone:${venueId}`);
				persisted = raw ? JSON.parse(raw) : null;
			} catch {
				persisted = null;
			}
		}

		const barRoom = findBarRoomInZoneList(zones);

		if (persisted && persisted.selectedZone != null) {
			const z = persisted.selectedZone;
			const showBar = Boolean(persisted.showBarraList);
			if (z === "Otra Zona" || zones.includes(z)) {
				state.accountsZoneSelectedZone = z;
				state.accountsZoneShowBarraList = showBar;
				return;
			}
		}

		if (zones.includes(barRoom)) {
			state.accountsZoneSelectedZone = barRoom;
			state.accountsZoneShowBarraList = true;
		} else {
			state.accountsZoneSelectedZone = zones.length > 0 ? zones[0] : null;
			state.accountsZoneShowBarraList = false;
		}
	},

	applyAccountsZoneAfterWalkIn(state, { reservation, venueId }) {
		const room = reservation?.roomName;
		if (!room) {
			return;
		}
		state.accountsZoneSelectedZone = room;
		state.accountsZoneShowBarraList = isBarRoomReservation(reservation, null);
		persistAccountsZoneFilter(venueId, state.accountsZoneSelectedZone, state.accountsZoneShowBarraList);
	},

	resetState(state) {
		const sel = state.accountsZoneSelectedZone;
		const bar = state.accountsZoneShowBarraList;
		const searchByCategories = state.searchProductsByCategories;
		Object.assign(state, getDefaultState());
		state.accountsZoneSelectedZone = sel;
		state.accountsZoneShowBarraList = bar;
		state.searchProductsByCategories = searchByCategories;
	},

	setSelectedTables(state, tables) {
		state.selectedTables = tables;
	},
	toggleSelectedTable(state, table) {
		const index = state.selectedTables.findIndex((t) => t.table_id === table.table_id);
		if (index !== -1) {
			state.selectedTables.splice(index, 1);
		} else {
			state.selectedTables.push(table);
		}
	},
	clearSelectedTables(state) {
		state.selectedTables = [];
	},
};

const actions = {
	updateProductSelection({ commit }, { productId, isSelected }) {
		if (isSelected) {
			commit("addListProductsSelected", productId);
		} else {
			commit("removeListProductsSelected", productId);
		}
	},

	resetAllStates({ commit }) {
		commit("resetState");
		commit("copilot/setReservationId", null, { root: true });
		commit("copilot/setSelectedReservationId", { reservationId: null }, { root: true });
		commit("copilot/setCommanderReservation", { commanderReservation: null }, { root: true });
		commit("reservation/setReservation", null, { root: true });
		viewSectionStandar();
	},

	/** After walk-in API success when the menu cart already has lines — avoid full tpv reset / layout jump. */
	resetAfterWalkinSuccessNarrow({ commit }) {
		commit("copilot/setReservationId", null, { root: true });
		commit("copilot/setSelectedReservationId", { reservationId: null }, { root: true });
		commit("copilot/setCommanderReservation", { commanderReservation: null }, { root: true });
		commit("reservation/setReservation", null, { root: true });
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
